// TopicChipPicker — the Manual-mode chip wall used by MagicDesk and shared
// across Super Narrate / Deskeynomics. Surfaces:
//   • "From your interests"  — onboarding picks (always first, even if not
//                              subject-relevant)
//   • "Recommended for this lesson" — subject-relevant catalog picks
//   • "Discover more"        — collapsed-by-default category buckets
// Plus a free-text input for student-typed angles.
//
// Backend contract — `GET /api/student/autotune/topics?lesson_id=&kind=`:
//   { subject, your_picks: [...], recommended: [...], discover: [{category,label,topics}] }
//
// Every chip pick fires a signal to the ranker so the affinity score for
// that topic stays warm for the next generation.
import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Search } from 'lucide-react';
import api from '../../studentApi';
import { topicIcon, categoryAccent } from './autotuneIcons';

export default function TopicChipPicker({
  lessonId,
  kind,                  // 'magicdesk' | 'narrate' | 'deskeynomics'
  tint,
  value,                 // { id, label, custom? }  |  null
  onChange,              // (next: {id,label,custom} | null) => void
  onPendingCustomTextChange, // optional: (text: string) => void — fires on
                             //   every keystroke so the parent CTA can
                             //   treat un-confirmed typed text as a valid
                             //   interest without forcing "Use this".
  testIdPrefix = 'topic-picker',
}) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [customText, setCustomText] = useState(value?.custom ? value.label : '');

  // Bubble the typed text up to any parent that wants to react to it.
  useEffect(() => {
    onPendingCustomTextChange?.(customText);
  }, [customText, onPendingCustomTextChange]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/student/autotune/topics?lesson_id=${lessonId}&kind=${kind}`)
      .then(d => { if (alive) setCatalog(d); })
      .catch(() => alive && setCatalog(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [lessonId, kind]);

  const pickChip = (topic) => {
    const next = { id: topic.id, label: topic.label, emoji: topic.emoji, icon: topic.icon, category: topic.category, custom: false };
    onChange?.(next);
    setCustomText('');
    api.post('/student/autotune/signal', {
      signal: 'topic_chip_picked',
      lesson_id: lessonId,
      kind,
      topic_id: topic.id,
    }).catch(() => {});
  };

  const submitCustom = () => {
    const t = customText.trim();
    if (!t) return;
    const next = { id: `custom:${t.toLowerCase()}`, label: t, custom: true };
    onChange?.(next);
    api.post('/student/autotune/signal', {
      signal: 'manual_topic_typed',
      lesson_id: lessonId,
      kind,
      free_text: t,
    }).catch(() => {});
  };

  const clear = () => {
    onChange?.(null);
    setCustomText('');
  };

  if (loading) {
    return (
      <div data-testid={`${testIdPrefix}-loading`} className="rounded-xl border p-6 text-center text-[12px] opacity-60"
        style={{ borderColor: 'var(--st-border)', background: 'var(--st-card)' }}>
        Curating topics for this lesson…
      </div>
    );
  }
  if (!catalog) return null;

  return (
    <div data-testid={testIdPrefix} className="space-y-4">
      {/* Selection echo */}
      {value && (
        <SelectionEcho value={value} tint={tint} onClear={clear} testId={`${testIdPrefix}-selection`} />
      )}

      {/* Your picks */}
      {catalog.your_picks?.length > 0 && (
        <ChipSection
          title="From your interests"
          subtitle="The ones you chose during onboarding."
          chips={catalog.your_picks}
          selectedId={value?.id}
          onPick={pickChip}
          tint={tint}
          highlight
          testId={`${testIdPrefix}-your-picks`}
        />
      )}

      {/* Recommended */}
      {catalog.recommended?.length > 0 && (
        <ChipSection
          title="Recommended for this lesson"
          subtitle={catalog.subject && catalog.subject !== 'general'
            ? `Hand-matched to the ${catalog.subject.replace('_', ' ')} concepts in this chapter.`
            : 'Curated topics that pair well with this chapter.'}
          chips={catalog.recommended}
          selectedId={value?.id}
          onPick={pickChip}
          tint={tint}
          testId={`${testIdPrefix}-recommended`}
        />
      )}

      {/* Free-text */}
      <CustomInput
        text={customText}
        setText={setCustomText}
        onSubmit={submitCustom}
        tint={tint}
        testIdPrefix={testIdPrefix}
        activeCustomLabel={value?.custom ? value.label : null}
      />

      {/* Discover more — collapsible */}
      {catalog.discover?.length > 0 && (
        <div>
          <button
            onClick={() => setDiscoverOpen(o => !o)}
            data-testid={`${testIdPrefix}-discover-toggle`}
            className="inline-flex items-center gap-1 text-[11.5px] font-bold opacity-80 hover:opacity-100"
            style={{ color: 'var(--st-ink)' }}>
            <Search className="w-3 h-3" />
            {discoverOpen ? 'Hide more topics' : `Discover more topics (${catalog.discover.reduce((a, b) => a + (b.topics?.length || 0), 0)})`}
          </button>
          {discoverOpen && (
            <div className="mt-3 space-y-4" data-testid={`${testIdPrefix}-discover-grid`}>
              {catalog.discover.map(bucket => (
                <ChipSection
                  key={bucket.category}
                  title={bucket.label}
                  chips={bucket.topics}
                  selectedId={value?.id}
                  onPick={pickChip}
                  tint={tint}
                  compact
                  testId={`${testIdPrefix}-cat-${bucket.category}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── sub-components ─────────────────────────────────────────────────────────
function ChipSection({ title, subtitle, chips, selectedId, onPick, tint, highlight, compact, testId }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-baseline gap-2 mb-2">
        <h4 className="text-[12.5px] font-extrabold" style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>{title}</h4>
        {highlight && (
          <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full"
            style={{ background: `color-mix(in srgb, ${tint} 18%, transparent)`, color: tint }}>For you</span>
        )}
      </div>
      {subtitle && <p className="text-[11px] mb-2.5 opacity-70" style={{ color: 'var(--st-ink)' }}>{subtitle}</p>}
      <div className={`flex flex-wrap gap-${compact ? '1.5' : '2'}`}>
        {chips.map(t => (
          <Chip key={t.id} topic={t}
            selected={selectedId === t.id}
            onClick={() => onPick(t)}
            tint={tint}
            compact={compact}
            testId={`${testId}-chip-${t.id}`} />
        ))}
      </div>
    </section>
  );
}

function Chip({ topic, selected, onClick, tint, compact, testId }) {
  const Icon = topicIcon(topic.icon);
  const accent = categoryAccent(topic.category);
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      data-selected={selected ? 'true' : 'false'}
      aria-pressed={selected}
      className={`relative group inline-flex items-center rounded-full transition-all duration-200
        ${selected ? 'scale-[1.04] -translate-y-0.5' : 'hover:scale-[1.03] hover:-translate-y-0.5'}`}
      style={{
        padding: compact ? '5px 11px 5px 7px' : '6px 13px 6px 8px',
        background: selected
          ? `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)`
          : 'var(--st-card)',
        border: '1.5px solid',
        borderColor: selected ? accent.from : 'var(--st-border)',
        color: selected ? '#fff' : 'var(--st-ink)',
        boxShadow: selected
          ? `0 8px 22px -10px ${accent.to}, 0 0 0 2px color-mix(in srgb, ${tint} 30%, transparent)`
          : 'none',
      }}>
      <span
        className={`shrink-0 rounded-full flex items-center justify-center mr-1.5 transition-all
          ${compact ? 'w-5 h-5' : 'w-6 h-6'}`}
        style={{
          background: selected
            ? 'rgba(255,255,255,0.22)'
            : `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)`,
          color: '#fff',
        }}>
        <Icon className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      </span>
      <span className={`font-bold ${compact ? 'text-[11px]' : 'text-[12px]'} whitespace-nowrap pr-0.5`}>
        {topic.label}
      </span>
      {selected && (
        <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full"
          style={{ background: 'rgba(255,255,255,0.25)' }}>
          <Check className="w-2.5 h-2.5" />
        </span>
      )}
    </button>
  );
}

function CustomInput({ text, setText, onSubmit, tint, testIdPrefix, activeCustomLabel }) {
  return (
    <section data-testid={`${testIdPrefix}-custom`}>
      <h4 className="text-[12.5px] font-extrabold mb-2" style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
        Or type your own
      </h4>
      <div className="flex gap-2">
        <div className="flex-1 flex items-center rounded-full border px-3.5 py-2 transition-all"
          style={{
            borderColor: text ? `color-mix(in srgb, ${tint} 60%, var(--st-border))` : 'var(--st-border)',
            background: 'var(--st-card)',
          }}>
          <Plus className="w-3.5 h-3.5 opacity-60 mr-2 shrink-0" />
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onSubmit(); }}
            data-testid={`${testIdPrefix}-custom-input`}
            placeholder="e.g. badminton, K-pop, vintage cars, climate change…"
            className="flex-1 bg-transparent text-[12.5px] outline-none placeholder:opacity-50"
            style={{ color: 'var(--st-ink)' }}
          />
        </div>
        <button
          onClick={onSubmit}
          disabled={!text.trim()}
          data-testid={`${testIdPrefix}-custom-add`}
          className="rounded-full px-4 py-2 text-[11.5px] font-bold text-white disabled:opacity-40 transition-transform hover:scale-105"
          style={{ background: tint }}>
          Use this
        </button>
      </div>
      {activeCustomLabel && (
        <div className="mt-1.5 text-[10.5px] opacity-70" style={{ color: 'var(--st-ink)' }}>
          Currently using your own angle: <b>{activeCustomLabel}</b>
        </div>
      )}
    </section>
  );
}

function SelectionEcho({ value, tint, onClear, testId }) {
  const Icon = topicIcon(value.icon);
  const accent = categoryAccent(value.category);
  return (
    <div data-testid={testId}
      className="rounded-xl border-2 px-3 py-2.5 flex items-center gap-3"
      style={{
        borderColor: accent.from,
        background: `linear-gradient(135deg, color-mix(in srgb, ${accent.from} 9%, var(--st-card)) 0%, var(--st-card) 100%)`,
      }}>
      <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
        style={{ background: `linear-gradient(135deg, ${accent.from} 0%, ${accent.to} 100%)` }}>
        {value.custom ? <Plus className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[9.5px] font-black uppercase tracking-widest opacity-70">Angle locked in</div>
        <div className="text-[13.5px] font-extrabold leading-tight" style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
          {value.emoji ? `${value.emoji} ` : ''}{value.label}
        </div>
      </div>
      <button
        onClick={onClear}
        data-testid={`${testId}-clear`}
        className="text-[11px] font-bold opacity-70 hover:opacity-100"
        style={{ color: tint }}>
        Change
      </button>
    </div>
  );
}
